import useSWR from "@/hooks/useSWR";
import { urlWithPlanId } from "@/utils/toPlanId";
import {
  AvailableSiteChecks,
  PlanCategorySubType,
  PlanCategoryType,
  PlanIdTypeSafe,
} from "@/types/PlanTypes";
import { SubscriptionPricingType } from "@/types/SubscriptionTypes";
import isProxyStockError from "@/utils/isProxyStockError";
import usePlanAccessResolver from "@/hooks/usePlanAccessResolver";
import { SubscriptionBehavior } from "@/types/subscriptionBehavior";
import { retryServerErrorsOnly } from "@/utils/swrRetry";

/**
 * Describes the representative plan whose price should be shown as a
 * "From $X / mo" / "Starting from" anchor. We resolve the price from the
 * `/subscription/pricing/` endpoint instead of hardcoding it so that backend
 * pricing changes (e.g. pricing experiments) are reflected automatically and
 * there is a single source of truth for prices.
 */
export type FromPriceConfig = {
  proxyType: PlanCategoryType;
  proxySubtype: PlanCategorySubType;
  /** Total proxy count (encoded as `proxy_countries: { ZZ: proxyCount }`). */
  proxyCount: number;
  bandwidth: number;
  term?: "monthly" | "yearly";
  requiredSiteChecks?: AvailableSiteChecks[];
  /**
   * How to price relative to the user's existing plans. Usually left unset:
   * `useFromPrice` resolves it from the user's plans (an owned plan of this
   * product → "upgrade" so existing-customer pricing applies, otherwise
   * "add"). Set explicitly only to override that.
   */
  behavior?: SubscriptionBehavior;
  /** Existing plan to price against (for "upgrade"). Defaults to "default". */
  planId?: PlanIdTypeSafe;
};

/**
 * Builds the `/subscription/pricing/` query for a representative plan config.
 * Mirrors the payload the customize page sends (see `formDataToPurchaseData`),
 * keeping the optional add-ons/refreshes at their no-op defaults so the
 * returned price reflects the bare plan.
 */
function buildPricingUrl(config: FromPriceConfig): string {
  const purchaseData = {
    proxy_type: config.proxyType,
    proxy_subtype: config.proxySubtype,
    proxy_countries: { ZZ: config.proxyCount },
    bandwidth_limit: config.bandwidth,
    automatic_refresh_frequency: 0,
    on_demand_refreshes_total: 0,
    proxy_replacements_total: 0,
    subusers_total: 3,
    is_unlimited_ip_authorizations: false,
    is_high_concurrency: false,
    is_high_priority_network: false,
    high_quality_ips_only: false,
    required_site_checks: config.requiredSiteChecks ?? [],
    term: config.term ?? "monthly",
    ...(config.behavior ? { behavior: config.behavior } : {}),
  };

  return urlWithPlanId("/subscription/pricing/", {
    query: JSON.stringify(purchaseData),
    plan_id: config.planId ?? "default",
  });
}

/**
 * Whether the price should be personalised to the viewer's own plans.
 *
 * A caller that sets `behavior` has already pinned the pricing context, so the
 * viewer's plans cannot change the answer. That is the public pricing page: it
 * has no signed-in viewer to personalise for, and asking for plans there would
 * only earn a 401.
 */
const personalisesPricing = (config: FromPriceConfig | null): boolean =>
  config !== null && config.behavior === undefined;

/**
 * SWR's retry policy for a price, which differs by context.
 *
 * A personalised price is one the dashboard asks for, and it has never retried.
 * A pinned price is the public pricing page's, and it has no other way back
 * from a failure: `revalidateOnFocus` and `revalidateIfStale` are both off, so
 * without a retry one blip leaves the cards loading until a fresh mount. It
 * renders outside the dashboard's `SWRConfig`, so it names the policy itself.
 */
const retryPolicy = (config: FromPriceConfig | null) =>
  personalisesPricing(config)
    ? { shouldRetryOnError: false }
    : { onErrorRetry: retryServerErrorsOnly };

/**
 * Resolves the price of a representative plan from the pricing API.
 *
 * Pass `null` to disable the request (e.g. when a yearly price is only needed
 * once a "Pay yearly" toggle is enabled).
 *
 * For `term: "yearly"` the returned values are the annual totals: divide by 12
 * for a per-month figure.
 *
 * `isOutOfStock` is `true` when the backend rejected the config because the
 * requested proxy count exceeds available stock, so callers can use it to mark
 * a plan tier as unavailable instead of showing a price.
 */
export default function useFromPrice(config: FromPriceConfig | null) {
  const resolveAccess = usePlanAccessResolver(personalisesPricing(config));

  // Resolve behavior/planId from the user's plans so prices reflect their
  // situation (existing-customer vs new). An explicit behavior/planId on the
  // config wins (so callers can still force a specific pricing context).
  const effectiveConfig: FromPriceConfig | null = config
    ? { ...resolveAccess(config.proxyType, config.proxySubtype), ...config }
    : null;

  const { data, isLoading, error } = useSWR<SubscriptionPricingType>(
    {
      url: effectiveConfig ? buildPricingUrl(effectiveConfig) : null,
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      keepPreviousData: true,
      ...retryPolicy(config),
    }
  );

  return {
    price: data?.price,
    nonDiscountedPrice: data?.non_discounted_price,
    // The volume-and-term discount the backend applied, taken rather than
    // recomputed from the two prices above so a display cannot round its way
    // into disagreeing with them. Not the R-1595 bundle offer, which is below.
    discountPercentage: data?.discount_percentage,
    // R-1595: isolated exclusive ISP→Residential bundle discount. Prefer this
    // over `discount_percentage` so term/count discounts can't be mistaken for
    // the exclusive offer (and it's only set for ISP owners on the 1 GB plan).
    bundleAddonDiscountPercentage: data?.bundle_addon_discount_percentage,
    isLoading,
    error,
    isOutOfStock: isProxyStockError(error),
  };
}
